import React from "react";
import { useSelector } from "react-redux";
import { ThemeProvider } from "styled-components/native";

import {
  useNavigation,
  useSurveyTheme,
  useFillPlaceholder,
} from "screens/question/hooks";
import { MarkdownText, ProgressBar, Slider, Loader } from "components";
import { Images } from "utils/Images";
import { ScreenType } from "consts";
import { useOrientation } from "utils";
import * as types from "./types";
import { FeedbackReviewQuestion } from "../themeA/types";

import {
  Container,
  BgImage,
  SectionWrapper,
  LogoImage,
  SectionInfoText,
  ContentWrapper,
  ButtonWrapper,
  NextButtonText,
  PrevButtonText,
  NextButton,
  PrevButton,
  ArrowImage,
  QuestionImageContainer,
  QuestionImageItem,
} from "./styled";

const screenTypeMap = {
  [ScreenType.CHECK_BOX]: types.CheckBoxQuestion,
  [ScreenType.CONSENT]: types.ConsentQuestion,
  [ScreenType.CONTACT_NUMBER]: types.ContactNumberQuestion,
  [ScreenType.DIVISION]: types.DivisionQuestion,
  [ScreenType.DRILLED_DOWN]: types.DrilledDownQuestion,
  [ScreenType.EMAIL_ADDRESS]: types.EmailAddressQuestion,
  [ScreenType.EMOJI_NPS]: types.EmojiNpsQuestion,
  [ScreenType.IMAGE]: types.ImageQuestion,
  [ScreenType.LABELLED_IMAGE]: types.LabeledImageQuestion,
  [ScreenType.LIST]: types.ListQuestion,
  [ScreenType.MEDIA]: types.MediaQuestion,
  [ScreenType.NPS]: types.ScoreQuestion,
  [ScreenType.STAR]: types.StarQuestion,
  [ScreenType.TEXT_AREA]: types.TextInputQuestion,
  [ScreenType.TEXT_INPUT]: types.TextInputQuestion,
  [ScreenType.TILE]: types.TileQuestion,
  [ScreenType.DESCRIPTION]: types.DescriptionQuestion,
  [ScreenType.ASSIGNEE]: types.AssigneeQuestion,
  [ScreenType.MCQS]: types.MultipleQuestion,
  [ScreenType.FEEDBACK_REVIEW]: FeedbackReviewQuestion,
};

const hideQuestionTitleSceens = {
  [ScreenType.CONSENT]: true,
  [ScreenType.TILE]: true,
  [ScreenType.DESCRIPTION]: true,
  [ScreenType.MCQS]: true,
};

const Section = ({ questionnaire }) => {
  const {
    RTL,
    section,
    questions,
    isValid,
    sequence,
    progress,
    navForward,
    navBackward,
    nextButton,
    previousButton,
  } = useNavigation(questionnaire);
  const is_submit_clicked = useSelector(
    state => state.navigation.is_submit_clicked,
  );
  const feedbackStatus = useSelector(state => state.feedback.status);

  const { logo, background, theme } = useSurveyTheme();

  useOrientation();

  if (is_submit_clicked && !("message" in feedbackStatus)) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <BgImage source={background} />
        {!questionnaire.config?.hide_progress_bar && (
          <ProgressBar progress={progress} />
        )}
        <LogoImage
          source={logo}
          $hideSm={questionnaire.config?.hide_logo_on_mobile}
        />
        <SectionWrapper sequence={sequence}>
          <Slider RTL={RTL} sequence={sequence}>
            {section.title && <Title>{section.title}</Title>}
            <ContentWrapper>
              {questions.map(question => {
                const Question =
                  screenTypeMap[question.app_screen_type] ||
                  types.CheckBoxQuestion;
                let childQuestions = [];
                if (question.is_master_question) {
                  childQuestions = section.questions.filter(
                    q => question.id === q.master_question,
                  );
                }

                return (
                  <>
                    <QuestionImages question={question} position="A" />
                    {question.is_statement_visible &&
                      !hideQuestionTitleSceens[question.app_screen_type] && (
                        <Title>{question.text}</Title>
                      )}
                    <QuestionImages question={question} position="B" />
                    {question.app_screen_type !== ScreenType.MCQS && (
                      <Question
                        key={question.id}
                        question={question}
                        section={section}
                        config={questionnaire.config}
                      />
                    )}
                    {question.is_master_question && (
                      <>
                        <Title>{question.text}</Title>
                        <Question
                          key={question.id}
                          question={question}
                          childQuestions={childQuestions}
                          section={section}
                          config={questionnaire.config}
                        />
                      </>
                    )}
                  </>
                );
              })}
            </ContentWrapper>
          </Slider>
        </SectionWrapper>
        <ButtonWrapper $RTL={RTL}>
          <PrevButton
            $hide={!previousButton}
            disabled={!previousButton}
            onPress={navBackward}
          >
            <PrevButtonText>{previousButton}</PrevButtonText>
            {previousButton && (
              <ArrowImage
                $arrow={"backArrow"}
                $back
                $hide={previousButton}
                source={RTL ? Images.RightArrow : Images.LeftArrow}
              />
            )}
          </PrevButton>
          <NextButton
            $isDisabled={!isValid}
            disabled={is_submit_clicked || !isValid}
            onPress={navForward}
          >
            <NextButtonText $isDisabled={!isValid}>{nextButton}</NextButtonText>
            <ArrowImage
              $arrow={"nextArrow"}
              $isDisabled={is_submit_clicked || !isValid}
              source={RTL ? Images.LeftArrow : Images.RightArrow}
            />
          </NextButton>
        </ButtonWrapper>
      </Container>
    </ThemeProvider>
  );
};

export const Title = ({ children }) => {
  const text = useFillPlaceholder(children);
  return (
    <SectionInfoText>
      <MarkdownText>{text}</MarkdownText>
    </SectionInfoText>
  );
};

export default Section;

const QuestionImages = ({ question, position }) => {
  const images =
    question.images?.filter(image => image.position === position) || [];

  if (!images.length) {
    return null;
  }

  return (
    <QuestionImageContainer>
      {images.map(image => (
        <QuestionImageItem
          key={image.id}
          $position={position}
          $width={image.width}
          $height={image.height}
          source={{ uri: image.image }}
        />
      ))}
    </QuestionImageContainer>
  );
};
